import React from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Breadcrumb {
  label: string
  href?: string
}

interface PageHeaderProps {
  title: string
  description?: string
  badge?: string
  badgeColor?: string
  breadcrumbs?: Breadcrumb[]
  backHref?: string
  backLabel?: string
  actions?: React.ReactNode
  className?: string
}

/**
 * Page Header with Fluent 2 styling and motion
 */
export function PageHeader({
  title,
  description,
  badge,
  badgeColor = 'from-violet-400 to-purple-600',
  breadcrumbs,
  backHref,
  backLabel = 'Back',
  actions,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn('mb-8', className)}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="flex items-center gap-2 text-sm mb-4">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.label}>
              {index > 0 && <ChevronRight className="h-4 w-4 text-[#a3a3a3]" />}
              {crumb.href ? (
                <Link
                  to={crumb.href}
                  className="text-[#616161] dark:text-[#adadad] hover:text-[#1a1a1a] dark:hover:text-white transition-colors duration-150 ease-[cubic-bezier(0.33,0,0.67,1)]"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-[#1a1a1a] dark:text-white font-medium">
                  {crumb.label}
                </span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}

      {/* Back button */}
      {backHref && (
        <Link
          to={backHref}
          className="inline-flex items-center gap-2 text-sm text-[#616161] dark:text-[#adadad] hover:text-[#1a1a1a] dark:hover:text-white transition-colors duration-150 ease-[cubic-bezier(0.33,0,0.67,1)] mb-4 group"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform duration-150" />
          {backLabel}
        </Link>
      )}

      {/* Header content */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-[28px] font-semibold tracking-[-0.02em] text-[#1a1a1a] dark:text-white">
              {title}
            </h1>
            {badge && (
              <span className={cn(
                'inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-white bg-gradient-to-r',
                'shadow-[0_2px_4px_rgba(0,0,0,0.14),0_0_2px_rgba(0,0,0,0.12)]',
                badgeColor
              )}>
                {badge}
              </span>
            )}
          </div>
          {description && (
            <p className="text-[#616161] dark:text-[#adadad]">
              {description}
            </p>
          )}
        </div>

        {actions && (
          <div className="flex items-center gap-3">
            {actions}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Fluent 2 styled card component for dashboard
 * Uses Fluent 2 shadows, radius, and motion
 */
interface FluentCardProps {
  children: React.ReactNode
  className?: string
  hover?: boolean
  onClick?: () => void
}

export function FluentCard({ children, className, hover = false, onClick }: FluentCardProps) {
  return (
    <div
      className={cn(
        'bg-white dark:bg-[#292929] rounded-xl border border-[#e5e5e5] dark:border-[#3d3d3d] p-6',
        'shadow-[0_2px_4px_rgba(0,0,0,0.04),0_0_2px_rgba(0,0,0,0.06)]',
        'dark:shadow-[0_2px_4px_rgba(0,0,0,0.2),0_0_2px_rgba(0,0,0,0.15)]',
        'transition-all duration-200 ease-[cubic-bezier(0.33,0,0.67,1)]',
        hover && [
          'cursor-pointer',
          'hover:shadow-[0_4px_8px_rgba(0,0,0,0.14),0_0_2px_rgba(0,0,0,0.12)]',
          'dark:hover:shadow-[0_4px_8px_rgba(0,0,0,0.3),0_0_2px_rgba(0,0,0,0.2)]',
          'hover:border-[#d4d4d4] dark:hover:border-[#525252]',
          'active:scale-[0.99]',
        ],
        className
      )}
      onClick={onClick}
    >
      {children}
    </div>
  )
}

/**
 * Stat card for dashboard with Fluent 2 styling
 */
interface StatCardProps {
  label: string
  value: string | number
  icon: React.ElementType
  color: string
  trend?: {
    value: number
    label: string
  }
  loading?: boolean
}

export function StatCard({ label, value, icon: Icon, color, trend, loading }: StatCardProps) {
  return (
    <FluentCard>
      <div className="flex items-start justify-between">
        <div className={cn(
          'p-3 rounded-xl bg-gradient-to-br',
          'shadow-[0_2px_4px_rgba(0,0,0,0.14),0_0_2px_rgba(0,0,0,0.12)]',
          color
        )}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        {trend && (
          <span className={cn(
            'text-xs font-medium px-2 py-1 rounded-full',
            trend.value >= 0
              ? 'bg-[#0e7a0d]/10 text-[#0e7a0d] dark:bg-[#54b054]/20 dark:text-[#54b054]'
              : 'bg-[#c50f1f]/10 text-[#c50f1f] dark:bg-[#dc626d]/20 dark:text-[#dc626d]'
          )}>
            {trend.value >= 0 ? '+' : ''}{trend.value}%
          </span>
        )}
      </div>
      <div className="mt-4">
        {loading ? (
          <div className="h-8 w-20 skeleton rounded-lg" />
        ) : (
          <p className="text-3xl font-semibold tracking-[-0.02em] text-[#1a1a1a] dark:text-white">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
        )}
        <p className="text-sm text-[#616161] dark:text-[#adadad] mt-1">{label}</p>
      </div>
    </FluentCard>
  )
}
